import { useState, useEffect } from "react";
import { CanalTV } from "@/entities/CanalTV";
import { Video } from "@/entities/Video";
import { AudioMP3 } from "@/entities/AudioMP3";
import { Tv, Play, Music, Plus, Pencil, Trash2, Save, X, ChevronDown, Check } from "lucide-react";

const TABS = [
  { id: "canales", label: "Canales TV", icon: Tv },
  { id: "videos", label: "Videos", icon: Play },
  { id: "audios", label: "Audios MP3", icon: Music },
];

const CANAL_CATEGORIAS = ["noticias", "entretenimiento", "deportes", "musica", "peliculas", "infantil", "otro"];
const CANAL_TIPOS = ["youtube", "m3u8", "iframe", "otro"];
const VIDEO_CATEGORIAS = ["musica", "entretenimiento", "noticias", "deportes", "educacion", "otro"];
const VIDEO_TIPOS = ["youtube", "mp4", "iframe", "otro"];
const AUDIO_GENEROS = ["pop", "rock", "reggaeton", "salsa", "cumbia", "vallenato", "electronica", "clasica", "otro"];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative bg-gray-950 border border-yellow-500/30 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold text-gray-300">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-yellow-500/60";
const selectCls = "w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-yellow-500/60";

// ─── CANALES ────────────────────────────────────────────────────────────────
function CanalForm({ inicial, onGuardar, onCerrar }) {
  const [form, setForm] = useState(inicial || { nombre: "", descripcion: "", url_stream: "", tipo_stream: "youtube", imagen_portada: "", categoria: "entretenimiento", activo: true, orden: 0 });
  const [guardando, setGuardando] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (!form.nombre || !form.url_stream) return alert("Nombre y URL son obligatorios");
    setGuardando(true);
    await onGuardar(form);
    setGuardando(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <Field label="Nombre del canal *"><input className={inputCls} value={form.nombre} onChange={e => set("nombre", e.target.value)} placeholder="Ej: Canal Noticias" /></Field>
      <Field label="URL del stream *"><input className={inputCls} value={form.url_stream} onChange={e => set("url_stream", e.target.value)} placeholder="https://youtube.com/watch?v=..." /></Field>
      <Field label="Tipo de stream">
        <select className={selectCls} value={form.tipo_stream} onChange={e => set("tipo_stream", e.target.value)}>
          {CANAL_TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Categoría">
        <select className={selectCls} value={form.categoria} onChange={e => set("categoria", e.target.value)}>
          {CANAL_CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Imagen de portada (URL)"><input className={inputCls} value={form.imagen_portada} onChange={e => set("imagen_portada", e.target.value)} placeholder="https://..." /></Field>
      <Field label="Descripción"><textarea className={inputCls} rows={2} value={form.descripcion} onChange={e => set("descripcion", e.target.value)} placeholder="Descripción del canal..." /></Field>
      <Field label="Orden"><input type="number" className={inputCls} value={form.orden} onChange={e => set("orden", parseInt(e.target.value) || 0)} /></Field>
      <div className="flex items-center gap-2">
        <button onClick={() => set("activo", !form.activo)}
          className={`w-10 h-6 rounded-full transition-colors ${form.activo ? "bg-yellow-400" : "bg-gray-700"}`}>
          <div className={`w-4 h-4 bg-white rounded-full mx-1 transition-transform ${form.activo ? "translate-x-4" : ""}`} />
        </button>
        <span className="text-sm text-gray-400">Canal activo</span>
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={onCerrar} className="flex-1 py-2.5 rounded-xl border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm font-semibold">Cancelar</button>
        <button onClick={guardar} disabled={guardando}
          className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2">
          {guardando ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar
        </button>
      </div>
    </div>
  );
}

// ─── VIDEOS ─────────────────────────────────────────────────────────────────
function VideoForm({ inicial, onGuardar, onCerrar }) {
  const [form, setForm] = useState(inicial || { titulo: "", descripcion: "", url_video: "", tipo_video: "youtube", thumbnail: "", categoria: "musica", duracion: "", activo: true, orden: 0 });
  const [guardando, setGuardando] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (!form.titulo || !form.url_video) return alert("Título y URL son obligatorios");
    setGuardando(true);
    await onGuardar(form);
    setGuardando(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <Field label="Título del video *"><input className={inputCls} value={form.titulo} onChange={e => set("titulo", e.target.value)} placeholder="Ej: Mi Video Musical" /></Field>
      <Field label="URL del video *"><input className={inputCls} value={form.url_video} onChange={e => set("url_video", e.target.value)} placeholder="https://youtube.com/watch?v=..." /></Field>
      <Field label="Tipo de video">
        <select className={selectCls} value={form.tipo_video} onChange={e => set("tipo_video", e.target.value)}>
          {VIDEO_TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Categoría">
        <select className={selectCls} value={form.categoria} onChange={e => set("categoria", e.target.value)}>
          {VIDEO_CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Miniatura (URL)"><input className={inputCls} value={form.thumbnail} onChange={e => set("thumbnail", e.target.value)} placeholder="https://..." /></Field>
      <Field label="Duración (ej: 3:45)"><input className={inputCls} value={form.duracion} onChange={e => set("duracion", e.target.value)} placeholder="3:45" /></Field>
      <Field label="Descripción"><textarea className={inputCls} rows={2} value={form.descripcion} onChange={e => set("descripcion", e.target.value)} placeholder="Descripción del video..." /></Field>
      <Field label="Orden"><input type="number" className={inputCls} value={form.orden} onChange={e => set("orden", parseInt(e.target.value) || 0)} /></Field>
      <div className="flex items-center gap-2">
        <button onClick={() => set("activo", !form.activo)}
          className={`w-10 h-6 rounded-full transition-colors ${form.activo ? "bg-yellow-400" : "bg-gray-700"}`}>
          <div className={`w-4 h-4 bg-white rounded-full mx-1 transition-transform ${form.activo ? "translate-x-4" : ""}`} />
        </button>
        <span className="text-sm text-gray-400">Video activo</span>
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={onCerrar} className="flex-1 py-2.5 rounded-xl border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm font-semibold">Cancelar</button>
        <button onClick={guardar} disabled={guardando}
          className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2">
          {guardando ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar
        </button>
      </div>
    </div>
  );
}

// ─── AUDIOS ─────────────────────────────────────────────────────────────────
function AudioForm({ inicial, onGuardar, onCerrar }) {
  const [form, setForm] = useState(inicial || { titulo: "", artista: "", album: "", url_audio: "", imagen_portada: "", genero: "otro", duracion: "", activo: true, orden: 0 });
  const [guardando, setGuardando] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (!form.titulo || !form.url_audio) return alert("Título y URL son obligatorios");
    setGuardando(true);
    await onGuardar(form);
    setGuardando(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <Field label="Título *"><input className={inputCls} value={form.titulo} onChange={e => set("titulo", e.target.value)} placeholder="Nombre de la canción" /></Field>
      <Field label="URL del audio MP3 *"><input className={inputCls} value={form.url_audio} onChange={e => set("url_audio", e.target.value)} placeholder="https://...archivo.mp3" /></Field>
      <Field label="Artista"><input className={inputCls} value={form.artista} onChange={e => set("artista", e.target.value)} placeholder="Nombre del artista" /></Field>
      <Field label="Álbum"><input className={inputCls} value={form.album} onChange={e => set("album", e.target.value)} placeholder="Nombre del álbum" /></Field>
      <Field label="Género">
        <select className={selectCls} value={form.genero} onChange={e => set("genero", e.target.value)}>
          {AUDIO_GENEROS.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </Field>
      <Field label="Imagen de portada (URL)"><input className={inputCls} value={form.imagen_portada} onChange={e => set("imagen_portada", e.target.value)} placeholder="https://..." /></Field>
      <Field label="Duración (ej: 3:45)"><input className={inputCls} value={form.duracion} onChange={e => set("duracion", e.target.value)} placeholder="3:45" /></Field>
      <Field label="Orden"><input type="number" className={inputCls} value={form.orden} onChange={e => set("orden", parseInt(e.target.value) || 0)} /></Field>
      <div className="flex items-center gap-2">
        <button onClick={() => set("activo", !form.activo)}
          className={`w-10 h-6 rounded-full transition-colors ${form.activo ? "bg-yellow-400" : "bg-gray-700"}`}>
          <div className={`w-4 h-4 bg-white rounded-full mx-1 transition-transform ${form.activo ? "translate-x-4" : ""}`} />
        </button>
        <span className="text-sm text-gray-400">Audio activo</span>
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={onCerrar} className="flex-1 py-2.5 rounded-xl border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm font-semibold">Cancelar</button>
        <button onClick={guardar} disabled={guardando}
          className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2">
          {guardando ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar
        </button>
      </div>
    </div>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function Admin() {
  const [tab, setTab] = useState("canales");
  const [canales, setCanales] = useState([]);
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { tipo, item? }
  const [confirmDelete, setConfirmDelete] = useState(null);

  const cargar = async () => {
    setLoading(true);
    const [c, v, a] = await Promise.all([
      CanalTV.list("orden", 100),
      Video.list("orden", 100),
      AudioMP3.list("orden", 100),
    ]);
    setCanales(c); setVideos(v); setAudios(a);
    setLoading(false);
  };

  useEffect(() => { cargar(); }, []);

  // Guardar canal
  const guardarCanal = async (form) => {
    if (modal.item) await CanalTV.update(modal.item.id, form);
    else await CanalTV.create(form);
    setModal(null); cargar();
  };

  // Guardar video
  const guardarVideo = async (form) => {
    if (modal.item) await Video.update(modal.item.id, form);
    else await Video.create(form);
    setModal(null); cargar();
  };

  // Guardar audio
  const guardarAudio = async (form) => {
    if (modal.item) await AudioMP3.update(modal.item.id, form);
    else await AudioMP3.create(form);
    setModal(null); cargar();
  };

  // Eliminar
  const eliminar = async () => {
    if (!confirmDelete) return;
    if (confirmDelete.tipo === "canal") await CanalTV.delete(confirmDelete.id);
    if (confirmDelete.tipo === "video") await Video.delete(confirmDelete.id);
    if (confirmDelete.tipo === "audio") await AudioMP3.delete(confirmDelete.id);
    setConfirmDelete(null); cargar();
  };

  const datos = tab === "canales" ? canales : tab === "videos" ? videos : audios;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="border-b border-yellow-500/20 px-4 py-4">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-xl font-bold text-white">Panel de Administración</h1>
          <p className="text-gray-500 text-sm mt-0.5">Gestiona canales, videos y audios de LUISMUSICTVS</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-gray-950 p-1 rounded-xl border border-gray-800 w-fit">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  tab === t.id ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-black" : "text-gray-400 hover:text-white"
                }`}>
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Botón agregar */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-500 text-sm">{datos.length} elemento{datos.length !== 1 ? "s" : ""}</p>
          <button
            onClick={() => setModal({ tipo: tab === "canales" ? "canal" : tab === "videos" ? "video" : "audio" })}
            className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-4 py-2 rounded-xl text-sm hover:opacity-90 transition-opacity">
            <Plus className="w-4 h-4" />
            Agregar {tab === "canales" ? "Canal" : tab === "videos" ? "Video" : "Audio"}
          </button>
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : datos.length === 0 ? (
          <div className="text-center py-16 text-gray-600 border border-dashed border-gray-800 rounded-2xl">
            <p className="text-lg">No hay elementos aún</p>
            <p className="text-sm mt-1">Haz clic en "Agregar" para comenzar</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {datos.map(item => {
              const nombre = item.nombre || item.titulo;
              const sub = item.descripcion || item.artista || item.categoria || item.genero || "";
              const img = item.imagen_portada || item.thumbnail;
              const activo = item.activo !== false;
              return (
                <div key={item.id}
                  className="flex items-center gap-3 p-3 bg-gray-950 rounded-xl border border-gray-800 hover:border-gray-700 transition-colors">
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800 flex items-center justify-center">
                    {img ? <img src={img} alt={nombre} className="w-full h-full object-cover" /> :
                      tab === "canales" ? <Tv className="w-5 h-5 text-gray-600" /> :
                      tab === "videos" ? <Play className="w-5 h-5 text-gray-600" /> :
                      <Music className="w-5 h-5 text-gray-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{nombre}</p>
                    <p className="text-gray-500 text-xs truncate capitalize">{sub}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${activo ? "bg-green-500/20 text-green-400" : "bg-gray-800 text-gray-500"}`}>
                      {activo ? "Activo" : "Inactivo"}
                    </span>
                    <button
                      onClick={() => setModal({ tipo: tab === "canales" ? "canal" : tab === "videos" ? "video" : "audio", item })}
                      className="p-2 text-gray-500 hover:text-yellow-400 transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setConfirmDelete({ id: item.id, tipo: tab === "canales" ? "canal" : tab === "videos" ? "video" : "audio", nombre })}
                      className="p-2 text-gray-500 hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal formulario */}
      {modal && (
        <Modal
          title={`${modal.item ? "Editar" : "Agregar"} ${modal.tipo === "canal" ? "Canal TV" : modal.tipo === "video" ? "Video" : "Audio MP3"}`}
          onClose={() => setModal(null)}
        >
          {modal.tipo === "canal" && <CanalForm inicial={modal.item} onGuardar={guardarCanal} onCerrar={() => setModal(null)} />}
          {modal.tipo === "video" && <VideoForm inicial={modal.item} onGuardar={guardarVideo} onCerrar={() => setModal(null)} />}
          {modal.tipo === "audio" && <AudioForm inicial={modal.item} onGuardar={guardarAudio} onCerrar={() => setModal(null)} />}
        </Modal>
      )}

      {/* Modal confirmar eliminar */}
      {confirmDelete && (
        <Modal title="Confirmar eliminación" onClose={() => setConfirmDelete(null)}>
          <p className="text-gray-300 mb-6">¿Estás seguro de que deseas eliminar <span className="text-white font-bold">"{confirmDelete.nombre}"</span>? Esta acción no se puede deshacer.</p>
          <div className="flex gap-3">
            <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 rounded-xl border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm font-semibold">Cancelar</button>
            <button onClick={eliminar} className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm transition-colors flex items-center justify-center gap-2">
              <Trash2 className="w-4 h-4" /> Eliminar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}